import React from 'react';
import { hasErrorCode } from '@/utils/errors';
import NotAuthorised from '@/components/errors/NotAuthorised';
import errorCodes from '@/constants/errors';
import findResult, { hasKey } from '@/utils/resultsHelper';
import { readStates } from '@/constants/lifecycle';
import urls, { relativeUrl, startsWithUrl, userGroupUrl } from '@/constants/urls';
import { fetchUserGroup, updateUsergroupPanels } from '../actions/usergroups/read';
import { updateUserGroupProperties } from '../actions/usergroups/update';
import { newUserGroupFromSource } from '../actions/usergroups/create';
import { isUserGroupDuplicateUrl } from '../helper';
import UserGroupPanelContainerGuarded from './UserGroupPanelContainerGuarded';

const UserGroupPanelContainer = ({
  canCreate,
  canRead,
  category,
  children,
  code,
  description,
  fetchGroup,
  isDirty,
  isDuplicate,
  isNew,
  name,
  newGroup,
  onChange,
  readErrors,
  readStatus,
  url,
}) => {
  if (!canRead || hasErrorCode(readErrors, errorCodes.forbidden) || (isNew && !canCreate)) {
    return <NotAuthorised />;
  }

  return (
    <UserGroupPanelContainerGuarded
      canRead={canRead}
      category={category}
      code={code}
      description={description}
      fetchGroup={fetchGroup}
      isDirty={isDirty}
      isDuplicate={isDuplicate}
      isNew={isNew}
      name={name}
      newGroup={newGroup}
      onChange={onChange}
      readErrors={readErrors}
      readStatus={readStatus}
      url={url}
    >
      {children}
    </UserGroupPanelContainerGuarded>
  );
};

const accessControl = (role, exists, isNew) => ({
  exists,
  isNew,
  canCreate: role.permissions.usergroups.create,
  canRead: role.permissions.usergroups.read,
});

const withoutItem = (role, url) => ({
  ...readStates,
  ...accessControl(role, false, !startsWithUrl(url, relativeUrl(urls.USER_GROUPS))),
  isDuplicate: isUserGroupDuplicateUrl(url),
  users: [],
  panels: [],
});

const withItem = (item, role, url) => {
  const isNewExists = hasKey(item, 'isNew');
  const genuineUserGroupUrl = startsWithUrl(url, relativeUrl(urls.USER_GROUPS));
  const urlForUserGroup = isNewExists && !item.isNew ? userGroupUrl(item) : url;
  const isNew = isNewExists ? item.isNew : !genuineUserGroupUrl;
  const isDuplicate = isUserGroupDuplicateUrl(url);

  return {
    bpmn: item.bpmn,
    code: item.code,
    readStatus: item.readStatus,
    readErrors: item.readErrors,
    saveErrors: item.saveErrors,
    description: item.description,
    name: item.name,
    metrics: item.metrics,
    isNew,
    isDirty: item.isDirty,
    isDuplicate,
    isEditing: item.isEditing,
    url: urlForUserGroup,
    users: [],
    panels: item.panels || [],
    ...accessControl(role, true, isNew),
  };
};

export const mapStateToProps = ({ auth: { role }, userGroups: { userGroups } }, { category, code, url }) => {
  const item = findResult(userGroups, category, code) || findResult(userGroups, category, code, 'previousCode');

  return item ? withItem(item, role, url) : withoutItem(role, url);
};

export const mapDispatchToProps = {
  fetchGroup: fetchUserGroup,
  newGroup: newUserGroupFromSource,
  updateProperties: updateUserGroupProperties,
  updatePanels: updateUsergroupPanels,
};

export default UserGroupPanelContainer;
